import React, { useState } from 'react';
import _ from 'lodash';
import {
  Box,
  Link,
  Button,
  Typography
} from '@mui/material';
import moment from 'moment';
import {
  lockIcon
} from '../../components/common';
import { useParams } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { levelTypes } from '../../../../utils/constants';
import DescriptionModal from '../../components/DescriptionModal';
import CheckableTickIcon from '../../../../components/CheckableTickIcon';

interface Task {
  id: string;
  _id: string;
  title: string;
  noteCount?: number;
  taskDescription: string;
  videoCallCount?: number;
  objectiveNumber?: number;
  totalObjectives?: number;
  status: 'todo' | 'inprogress' | 'completed';
  taskName: string;
  startDate: number;
  endDate: number;
}

interface IProps {
  task?: Task;
  levelName: string;
  levelId: string;
  borderColor?: string;
  trophyColor?: string;
  isLocked?: boolean;
  levelNumber?: number;
  levelDescription?: string;
  level: any;
}

const NewTaskCard: React.FC<IProps> = ({
  task,
  levelName,
  levelId,
  borderColor,
  trophyColor,
  isLocked,
  levelNumber,
  levelDescription,
  level
}) => {
  const navigate = useNavigate();
  const { taskId } = useParams() || '';
  const currentDate = Date.now() / 1000;
  const [descriptionModalOpen, setDescriptionModalOpen] = useState(false);

  /** Extract text from HTML */
  const extractTextFromHTML = (html: any) => {
    // Create a temporary DOM element
    const doc = new DOMParser().parseFromString(html, 'text/html');

    // Remove all script and style elements
    const scripts = doc.getElementsByTagName('script');
    const styles = doc.getElementsByTagName('style');

    Array.from(scripts).forEach((script: any) => script.remove());
    Array.from(styles).forEach((style: any) => style.remove());

    // Remove all images
    const images = doc.getElementsByTagName('img');
    Array.from(images).forEach((img: any) => img.remove());

    // Get text content
    let text = doc.body.textContent || '';

    // Clean up whitespace
    text = text.replace(/\s+/g, ' ').trim();

    return text;
  };

  /** Get Test */
  const textOnly = extractTextFromHTML(task?.taskDescription || '');

  /** level Availability check */
  const getLevelText = (level: any) => {
    if (level?.startDate?.type === levelTypes.availableSoon) {
      return 'soon';
    }
    return `on ${moment.unix(level?.startDate.value).format('DD/MM/YYYY')}`;
  };

  /** Task date Availability check  */
  const isTaskAvailable = !task?.startDate || currentDate >= task.startDate;

  /** View more and lesson page redirect */
  const handleNavigate = () => {
    if (isTaskAvailable) {
      navigate(`/app/home/mentee/level/${levelId}/task/${task?._id}`);
    }
  };

  /** Start date and end date for lesson and Level old */
  function getAvailabilityMessage(startDate: any, endDate: any, type: string, task?: any) {
    let color = '';
    let text = '';
    if (!startDate && !endDate) {
      text = '';
    } else if (startDate && !endDate) {
      if (currentDate >= startDate) {
        text = '';
      } else {
        text = `${moment.unix(startDate).format('DD/MM/YYYY')} `;
      }
    } else if (currentDate < startDate) {
      text = `${moment.unix(startDate).format('DD/MM/YYYY')} `;
    } else if (currentDate >= startDate && currentDate <= endDate) {
      const timeLeft = endDate - currentDate;
      if (timeLeft <= 7 * 24 * 3600) {
        // 7 days in seconds
        color = 'orange';
      } else {
        color = '';
      }
      text = `${moment.unix(endDate).format('DD/MM/YYYY')}`;
    } else if (currentDate > endDate) {
      color = 'red';
      text = `${moment.unix(endDate).format('DD/MM/YYYY')}`;
    } else {
      text = '';
    }

    if (type === 'level') {
      return (
        <Typography fontSize={'12px'} fontStyle={'italic'} color={color}>
          {text}
        </Typography>
      );
    }

    return (
      <Typography
        color={taskId === task?._id ? '#FFFFFF' : color}
        fontSize={'12px'}
        lineHeight={'14px'}
        fontStyle={'italic'}
      >
        {text}
      </Typography>
    );
  }

  /** Start date and end date for lesson */
  function getTaskStartEndDate(startDate: any, endDate: any) {
    let label = '';
    let dateText = '';

    if (startDate && endDate) {
      label = 'Start-End Date';
      dateText = `${moment.unix(startDate).format('DD/MM/YYYY')} - ${moment.unix(endDate).format('DD/MM/YYYY')}`;
    } else if (startDate) {
      label = 'Start Date';
      dateText = moment.unix(startDate).format('DD/MM/YYYY');
    } else if (endDate) {
      label = 'End Date';
      dateText = moment.unix(endDate).format('DD/MM/YYYY');
    }

    return (
      <Box mt={.3}>
        {dateText ? (
          <>
            <Typography fontSize="8px" fontStyle="italic">{label}</Typography>
            <Typography fontSize="10px" fontStyle="italic" fontWeight={600}>{dateText}</Typography>
          </>
        ) : (
          <Box height={'27px'} />
        )}
      </Box>
    );
  }

  /** Card colors */
  const statusColor = !isTaskAvailable ? '#ABB5BE' : borderColor === 'green' ? '#11895E' : '#0071A9';

  return (
    <>
      <Box key={task?.id} display="flex" className="taskCard" flexDirection="column" justifyContent="space-between">
        <Box flex={1} display={'flex'} flexDirection={'column'} height="100%">
          <Box borderRadius="8px 8px 0 0" borderTop={`10px solid ${statusColor}`} />
          <Box px={2} pt={1} pb={1}>
            <Box display="flex" justifyContent="space-between" alignItems="center" gap={'8px'}>
              <Typography noWrap color="secondary" variant="h6" flex={1}>
                {levelName}
              </Typography>
              {/* Level end date */}
              {/* {getAvailabilityMessage(level?.startDate?.value, level?.endDate?.value, 'level')} */}
              {/* Show the check icon only for mentorship */}
              <CheckableTickIcon checked={trophyColor === 'green'} />
            </Box>
            {/* Task name */}
            <Box display={'flex'} justifyContent={'space-between'} alignItems={'baseline'} gap={'8px'}>
              <Typography
                noWrap
                variant="h5"
                flexShrink={1}
                className="inline-fill"
                fontStyle={task?.title ? '' : 'italic'}
                color={task?.title ? statusColor : 'secondary.light'}
              >
                {task?.taskName}
              </Typography>
              {/* Lesson end date */}
              {/* {getAvailabilityMessage(task?.startDate, task?.endDate, 'task', task)} */}
            </Box>
            {/* Task start and end date */}
            {!isLocked && getTaskStartEndDate(task?.startDate, task?.endDate)}
          </Box>

          {/* Description section */}
          {isLocked ? (
            <Box px={2}>
              <Box textAlign={'center'} py={isLocked ? 4 : 0}>
                {lockIcon({ size: '40px', color: '#68717A' })}
                <Typography mt={1}>This level will be available {getLevelText(level)}</Typography>
                <Link
                  color={'info.main'}
                  fontStyle={'italic'}
                  onClick={() => {
                    setDescriptionModalOpen(true);
                  }}
                >
                  Read description of Module {levelNumber}
                </Link>
              </Box>
            </Box>
          ) : (
            <Box px={2} flex={1} display={'flex'} flexDirection={'column'} gap={1}>
              <Box flex={1}>
                <Typography className={'lineClamp-5'}>
                  {textOnly}
                  {/* {textOnly.length > 150 ? `${_.truncate(textOnly, { length: 150 })}` : textOnly} */}
                </Typography>
              </Box>

              {/* View more btn */}
              {!isLocked && (
                <Button
                  onClick={handleNavigate}
                  disabled={!isTaskAvailable}
                  className='p-0 fs-14 hover-none mr-auto'
                >
                  View more
                </Button>
              )}
            </Box>
          )}
        </Box>

        {/* Action Button */}
        {!isLocked && (
          <Box alignItems="flex-end" justifyContent="center" display="flex" p={2}>
            <Button
              variant="contained"
              className="w-100 fs-14"
              onClick={handleNavigate}
              aria-label={`View ${task?.status === 'completed' ? 'Details' : 'Progress'}`}
              disabled={!isTaskAvailable}
              sx={{
                backgroundColor: trophyColor === 'green' ? '#11895E' : 'info.main'
              }}
            >
              {trophyColor === 'green' ? 'Lesson Complete' : 'Complete Lesson'}
            </Button>
          </Box>
        )}
      </Box>
      {/* Description Modal */}
      <DescriptionModal
        open={descriptionModalOpen}
        onClose={() => setDescriptionModalOpen(false)}
        // title={`Module ${levelNumber} Description`}
        title={`${levelName}`}
        message={levelDescription}
      />
    </>
  );
};

export default NewTaskCard;
